import { useTranslation } from 'react-i18next';
import { AppModal } from '@components/form/Modal';
import { PaymentMethodsList } from '@modules/settings/subscription/components/payment-methods-list';
import { formatAmount, formatDate, centsToDollar } from '@modules/settings/subscription/utils/utils';
import { GetPaymentOrderByIdQuery } from '@/types/generated/graphql';
import { formatOrderType } from './confirm-payment.helpers';

type PaymentOrder = NonNullable<GetPaymentOrderByIdQuery['getPaymentOrderById']>['data'];

type Props = {
  open: boolean;
  loading: boolean;
  paymentOrder: PaymentOrder | null | undefined;
  onClose: VoidFunction;
  onCancelClick: VoidFunction;
  onContinue: VoidFunction;
};

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-center py-3 border-b border-gray-200 last:border-b-0">
      <p className="text-text-500 text-xs font-medium uppercase tracking-widest">{label}</p>
      <p className="text-text-800 text-sm font-medium">{value}</p>
    </div>
  );
}

function AmountRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-center py-3 border-b border-gray-200 last:border-b-0">
      <p className="text-text-500 text-xs font-medium uppercase tracking-widest">{label}</p>
      <p className="text-text-800 text-2xl font-medium">{value}</p>
    </div>
  );
}

export function ReviewPaymentModal({ open, loading, paymentOrder, onClose, onCancelClick, onContinue }: Props) {
  const { t } = useTranslation();

  const amount = formatAmount(centsToDollar(paymentOrder?.chargedAmount ?? paymentOrder?.cartAmount ?? 0));

  return (
    <AppModal
      width={450}
      title={
        <AppModal.Header>
          <AppModal.Title>{t('review_payment_request')}</AppModal.Title>
          <AppModal.SubTitle>{t('review_payment_request_description')}</AppModal.SubTitle>
        </AppModal.Header>
      }
      open={open}
      onCancel={onClose}
      footer={
        <div className="space-y-4">
          <AppModal.Footer>
            <AppModal.Button className="w-full capitalize!" onClick={onCancelClick}>
              {t('cancel_request')}
            </AppModal.Button>
            <AppModal.Button type="primary" className="w-full capitalize!" loading={loading} onClick={onContinue}>
              {t('continue_to_payment')}
            </AppModal.Button>
          </AppModal.Footer>
          <PaymentMethodsList.CompliantLabel />
        </div>
      }
    >
      <AppModal.Body>
        <p className="text-text-800 text-sm font-medium mb-2">{t('request_summary')}</p>
        <div className="border border-gray-200 rounded-lg px-4">
          <SummaryRow label={t('purpose')} value={formatOrderType(paymentOrder?.type) || '—'} />
          <SummaryRow
            label={t('created_at')}
            value={paymentOrder?.createdAt ? formatDate(paymentOrder.createdAt) : '—'}
          />
          <SummaryRow label={t('quantity')} value={String(paymentOrder?.itemsQuantity ?? '—')} />
          <AmountRow label={t('amount')} value={amount} />
          {paymentOrder?.remarks && <SummaryRow label={t('remarks')} value={paymentOrder.remarks} />}
        </div>
      </AppModal.Body>
    </AppModal>
  );
}
